/**
 * Shared editor-header controls used by both `<CodeBlock>` and `<NebulaCanvas>`.
 *
 * Each component is purely presentational — state lives with the parent.
 */

import React from "react";

/**
 * SaveButton — three-state save indicator (saving / saved / error / idle).
 * Hidden disabled state when there are lint errors.
 */
export function SaveButton({ status, onSave, disabled = false, hasErrors = false, className = "" }) {
  const isDisabled = disabled || status === "saving" || hasErrors;
  const stateClass =
    status === "saved"
      ? "bg-fill-green-100 text-ink-green-700 dark:bg-fill-green-900/40 dark:text-ink-green-300 border border-edge-green-200 dark:border-edge-green-800"
      : status === "error"
      ? "bg-fill-red-100 text-ink-red-700 dark:bg-fill-red-900/40 dark:text-ink-red-300 border border-edge-red-200 dark:border-edge-red-800"
      : "bg-fill-ink dark:bg-fill-editor-surface text-ink-white hover:opacity-80";
  const label =
    status === "saving"
      ? "Saving\u2026"
      : status === "saved"
      ? "\u2713 Saved"
      : status === "error"
      ? "Error"
      : "\u2191 Save";

  return (
    <button
      type="button"
      onClick={onSave}
      disabled={isDisabled}
      title={hasErrors ? "Fix errors before saving" : undefined}
      className={`photon-button-compact ${status === "saved" || status === "error" ? "" : "photon-button-primary photon-editor-save"} text-xs px-2.5 py-1 rounded-md font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${stateClass} ${className}`}
    >
      {label}
    </button>
  );
}

/**
 * ResetButton — clears local edits back to the original content.
 */
export function ResetButton({ onReset, disabled = false, className = "" }) {
  return (
    <button
      type="button"
      onClick={onReset}
      disabled={disabled}
      title={disabled ? "Accept or reject the AI edit first" : "Reset to last saved"}
      className={`photon-button-secondary photon-button-compact text-xs px-2 py-1 rounded-md text-ink-gray-500 dark:text-ink-gray-400 hover:text-ink-gray-700 dark:hover:text-ink-gray-200 bg-fill-white/80 dark:bg-fill-panel-surface/80 backdrop-blur-sm border border-edge-gray-200 dark:border-edge-editor-reset-border transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-ink-gray-500 dark:disabled:hover:text-ink-gray-400 ${className}`}
    >
      ↩ Reset
    </button>
  );
}

/**
 * PreviewCodeToggle — pill-shaped Preview / Code segmented control.
 */
export function PreviewCodeToggle({ showCode, onToggle, className = "", disabled = false }) {
  const baseBtn = "text-xs px-3 py-1 rounded-md font-medium transition-all";
  const activeCls = "bg-fill-white dark:bg-fill-editor-surface text-ink-ink dark:text-ink-white shadow-sm";
  const idleCls = "text-ink-gray-500 dark:text-ink-gray-400 hover:text-ink-gray-700 dark:hover:text-ink-gray-200";
  const disabledCls = disabled ? "opacity-50 cursor-not-allowed pointer-events-none" : "";
  return (
    <div className={`photon-segments flex items-center gap-0.5 bg-fill-gray-100 dark:bg-fill-panel-surface rounded-lg p-[3px] w-fit ${disabledCls} ${className}`}>
      <button
        type="button"
        disabled={disabled}
        aria-pressed={!showCode}
        onClick={(e) => { e.stopPropagation(); if (!disabled) onToggle(false); }}
        className={`${baseBtn} ${!showCode ? activeCls : idleCls}`}
      >
        Preview
      </button>
      <button
        type="button"
        disabled={disabled}
        aria-pressed={showCode}
        onClick={(e) => { e.stopPropagation(); if (!disabled) onToggle(true); }}
        className={`${baseBtn} ${showCode ? activeCls : idleCls}`}
      >
        Code
      </button>
    </div>
  );
}
